<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
    import { PaymentMethodsRepository } from '../../modules/posApi/payment-methods/payment-methods.repository'
    import { PaymentMethodsService } from '../../modules/posApi/payment-methods/payment-methods.service'
    import { PaymentMethod, PaymentMethodTypeEnum } from '../../modules/posApi/payment-methods/payment-methods'
    import type { CreatePaymentMethodsDto } from '../../modules/posApi/payment-methods/dto/create-payment-method.dto'
    import type { UpdatePaymentMethodsDto } from '../../modules/posApi/payment-methods/dto/update-payment-method.dto'
    import ObPosPaymentItem from './ob-pos-payment-item.svelte'
    import ObStep from './ob-step.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    const dispatch = createEventDispatcher()
    let min = 0
    let total = 0
    let methods = []
    let show = false
    let loaded = false
    const rep = new PaymentMethodsRepository()
    const service = new PaymentMethodsService(rep)

    // a lista começa no que já existe: não dá para ter menos meios do que os já cadastrados
    $: totalOptions = Array.from({ length: 15 }, (_, index) => ({ value: index + min, label: String(index + min) }))

    function totalChange() {
        while (methods.length != total) {
            if (methods.length > total) {
                methods.pop()
                continue
            }
            if (methods.length < total) {
                let s = new PaymentMethod()
                methods.push(s)
            }
        }
        methods = methods
    }

    function onBack() {
        if (methods.length > 0) {
            if (!confirm('As alterações dessa tela serão perdidas ao voltar. Deseja mesmo voltar?')) return false
        }
        dispatch('back')
    }

    async function onNext() {
        if (show) return
        if (await saveEntity()) {
            dispatch('next')
        }
    }

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            let result = await service.getAll()
            if (result.length === 0) {
                result.push({
                    label: 'Débito',
                    installments: 1,
                    type: PaymentMethodTypeEnum.debit,
                    receiptDelay: 0,
                    receiptInterval: 1,
                    receiptType: 'full',
                })
                result.push({
                    label: 'Crédito',
                    installments: 12,
                    type: PaymentMethodTypeEnum.credit,
                    receiptDelay: 30,
                    receiptInterval: 30,
                    receiptType: 'installment',
                })
                result.push({
                    label: 'Pix',
                    installments: 1,
                    type: PaymentMethodTypeEnum.pix,
                    receiptDelay: 0,
                    receiptInterval: 0,
                    receiptType: 'full',
                })
                result.push({
                    label: 'Dinheiro',
                    installments: 1,
                    type: PaymentMethodTypeEnum.money,
                    receiptDelay: 0,
                    receiptInterval: 0,
                    receiptType: 'full',
                })
                result.push({
                    label: 'Cheque',
                    installments: 12,
                    type: PaymentMethodTypeEnum.check,
                    receiptDelay: 1,
                    receiptInterval: 30,
                    receiptType: 'installment',
                })
            }
            min = result.length
            total = result.length
            methods = result
            loaded = true
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function createEntity(entity: PaymentMethod) {
        try {
            show = true

            const dto: CreatePaymentMethodsDto = {
                comissionFeeDebt: false,
                feePercentage: 0,
                installmentsFee: [{ fee: 0, installment: 1 }],
                label: entity.label,
                installments: entity.installments,
                billIntegration: entity.billIntegration,
                feeFixed: 0,
                receiptInterval: entity.receiptInterval,
                receiptDelay: entity.receiptDelay,
                receiptType: entity.receiptType,
                type: entity.type,
                comissionFeeDebtAttendant: false,
            }
            entity = await service.create(dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    async function updateEntity(entity) {
        try {
            show = true
            const dto: UpdatePaymentMethodsDto = {
                label: entity.label,
                installments: entity.installments,
            }
            entity = await service.update(entity.paymentMethodId, dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    function validateAll() {
        for (let entity of methods) {
            if (!validateFields(entity)) return false
        }
        return true
    }

    async function saveEntity() {
        methods = methods.filter((el) => !!el)
        if (!validateAll()) return false
        for (let entity of methods) {
            if (entity.paymentMethodId) {
                if (!(await updateEntity(entity))) return false
            } else {
                if (!(await createEntity(entity))) return false
            }
        }
        return true
    }

    function validateFields(entity) {
        if (!entity.label) {
            window.error('Informe o nome do meio de pagamento')
            return false
        }
        if (entity.installments < 1) {
            window.error('Informe o parcelamento máximo dos meios de pagamento')
            return false
        }
        return true
    }
</script>

<ObStep title="Meios de pagamento" label="Confirme os meios de pagamento aceitos por você">
    <svelte:fragment slot="acts">
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={onBack} />
        <Zbutton color="primary" icon={faAngleRight} label="Avançar" loading={show} disabled={show} on:click={onNext} />
    </svelte:fragment>

    <ZContainer gapRow="8px" gapCol="8px">
        <Zinput size={4} sizeM={12} label="Quantidade de meios aceitos" type="select" options={totalOptions} bind:value={total} on:change={totalChange} />
    </ZContainer>

    {#each methods as method}
        <ObPosPaymentItem bind:method />
    {/each}
</ObStep>

<LoadSpinner {show} />
